import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Backdrop from '@mui/material/Backdrop';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid2';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import Actions from 'components/Actions';
import AlertText from 'components/AlertText';
import ConditionalButton from 'components/ConditionalButton';
import { BasicDisplayUnits, unitLabels } from 'hooks/useDisplayUnits/useDisplayUnits';
import { i18n } from 'i18n';
import { Trans } from '@lingui/react';
import {
  ROUTE_PATH as NewPourRoutePath,
  ROUTE_PARAM_IDENTIFIER as projectParamIdentifier,
  RECIPE_PARAM_IDENTIFIER as recipeParamIdentifier,
  STOPE_PARAM_IDENTIFIER as stopeParamIdentifier
} from 'modules/project/NewPourPage/constants';
import ProjectAuthorise from 'modules/project/ProjectAuthorise';
import { useProject } from 'modules/project/providers/project';
import {
  BackfillRecipeDto,
  BackfillWarningDto,
  CustomUnitFields,
  FlushRecipeDto,
  ProjectModule,
  RheologyDataDto,
  UnitSystem,
  UpdateBackfillRecipeCommand,
  useDeleteFlushRecipeMutation,
  useDeleteRecipeMutation,
  useFlushRecipesQuery,
  useMineModelRheologyDataListQuery,
  useRecipesQuery,
  useRestoreFlushRecipeMutation,
  useRestoreRecipeMutation,
  useUpdateRecipeMutation
} from 'providers/api';
import { Permission } from 'providers/authorisation';
import { useConfirmation } from 'providers/confirm';
import { ConfirmationOptions } from 'providers/confirm/ConfirmDialog';
import { usePage } from 'providers/page';
import {
  indexBy,
  prop
} from 'ramda';
import React from 'react';
import { useScenario } from 'modules/project/providers/scenario';
import { useLocation, useNavigate } from 'react-router-dom';
import { resolveTemplate } from 'utils';
import { firstSelectableRheology } from 'utils/rheology';
import { pumpPressureKpaToBar } from 'utils/unitConversions';
import { airPushesWaterToStope, waterAloneReachesStope } from '../../MineModel/Flushing/flushOutcome';
import ListSkeleton from '../ListSkeleton';
import WarningTooltipIcon from '../WarningTooltipIcon';
import { applyRecipeModelVisuals, findStoredRoute, resetRoutesIfStoredRouteShown } from '../recipeModelUtils';
import FlushDetails from './FlushDetails';
import FlushForm from './FlushForm/FlushForm';
import { formatMinutes, pasteFromRecipe } from './FlushForm/flushFormHelpers';
import { FlushScrubContext } from './FlushScrub';
import RecipeDetails from './RecipeDetails';
import RecipeErroredPlaceHolder from './RecipeErroredPlaceHolder';
import RecipeForm from './RecipeForm/RecipeForm';
import { UnitSystemValue } from './UnitSystemDetail';
import { Model } from '../../hooks/useModelState';
import { returnShortestRoute } from '../../MineModel/Route/utils';
import { PCStope } from '../../MineModel/Stopes';
import { getPointsWithBoosterPressure } from '../../canvas/PCFunctions/PCPasteFunctions';

const PREFIX = 'RecipesTabPanel';

const classes = {
  root: `${PREFIX}-root`,
  headerTitle: `${PREFIX}-headerTitle`,
  alignRight: `${PREFIX}-alignRight`,
  deleteButton: `${PREFIX}-deleteButton`,
  accordianSummary: `${PREFIX}-accordianSummary`,
  accordionDetails: `${PREFIX}-accordionDetails`,
  backdrop: `${PREFIX}-backdrop`,
};

const Root = styled('div')((
  {
    theme,
  },
) => ({
  [`&.${classes.root}`]: {
    height: 'calc(100% - 190px)',
    overflowY: 'auto',
  },

  [`& .${classes.headerTitle}`]: {
    maxWidth: 175,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  [`& .${classes.alignRight}`]: {
    textAlign: 'right',
  },

  [`& .${classes.deleteButton}`]: {
    color: theme.palette.error.main,
  },

  [`& .${classes.accordianSummary}`]: {
    position: 'relative',
  },

  [`& .${classes.accordionDetails}`]: {
    display: 'block',
  },

  [`& .${classes.backdrop}`]: {
    backgroundColor: 'rgb(48 48 48 / 70%)',
    position: 'absolute',
    zIndex: 0,
  },
}));

interface RecipeStatusProps {
  isAutoRecipe: boolean | undefined;
  isStale: boolean;
  optimisedMassConcentration: number;
  selectedMassConcentration: number;
  optimisedBinderContent: number;
  selectedBinderContent: number;
}

const RecipeStatus = ({ isAutoRecipe, isStale, optimisedMassConcentration, selectedMassConcentration, optimisedBinderContent, selectedBinderContent }: RecipeStatusProps) => {
  if (isStale) {
    return <Typography color="textPrimary" variant="caption"><Trans id="Stale">Stale</Trans></Typography>;
  }

  const isOptimised = optimisedMassConcentration === selectedMassConcentration && optimisedBinderContent === selectedBinderContent;

  const status = `${isAutoRecipe ? i18n._('Auto Created').concat(' | ') : ''}${isOptimised ? i18n._('Optimised') : i18n._('Adjusted')}`;

  return <Typography color="textPrimary" variant="caption">{status}</Typography>;
};

interface RecipeSummaryProps {
  isAutoRecipe: boolean | undefined;
  warnings?: BackfillWarningDto[];
  reference: string;
  isStale: boolean;
  optimisedMassConcentration: number;
  selectedMassConcentration: number;
  optimisedBinderContent: number;
  selectedBinderContent: number;
}

const RecipeSummary = ({
  isAutoRecipe,
  warnings,
  reference,
  isStale,
  optimisedMassConcentration,
  selectedMassConcentration,
  optimisedBinderContent,
  selectedBinderContent,
}: RecipeSummaryProps) => (
  <Grid size={12} container spacing={1} sx={{ flexGrow: 1 }}>
    <Grid size={6} className={classes.headerTitle}>
      <Box sx={{
        display: 'flex',
      }}
      >
        {warnings && (
          <Box sx={{
            mr: 1,
          }}
          >
            <WarningTooltipIcon warnings={warnings} />
          </Box>
        )}
        <Box sx={{
          flexGrow: 1,
          wordBreak: 'break-word',
          overflowWrap: 'break-word',
          minWidth: 0, // Important for text wrapping inside a flexbox
        }}
        >
          <Typography sx={{ whiteSpace: 'normal' }}>
            {reference}
          </Typography>
        </Box>
      </Box>
    </Grid>
    <Grid size={6}>
      <Box sx={{ alignContent: 'center' }}>
        <RecipeStatus
          isAutoRecipe={isAutoRecipe}
          isStale={isStale}
          optimisedMassConcentration={optimisedMassConcentration}
          selectedMassConcentration={selectedMassConcentration}
          optimisedBinderContent={optimisedBinderContent}
          selectedBinderContent={selectedBinderContent}
        />
      </Box>
    </Grid>
  </Grid>
);

interface FlushSummaryProps {
  flushRecipe: FlushRecipeDto;
  unitSystemPreference: UnitSystem;
  customUnitFields: CustomUnitFields[];
}

const FlushSummary = ({ flushRecipe, unitSystemPreference, customUnitFields }: FlushSummaryProps) => {
  const { result } = flushRecipe;
  const warnings: BackfillWarningDto[] = result.warnings.map((warning) => ({ code: warning.code, display: warning.description }));
  const timeToWater = (() => {
    if (waterAloneReachesStope(result)) return formatMinutes(result.timeToWaterAtStopeMinutes as number);
    if (airPushesWaterToStope(result)) return i18n._('Air purge to stope');
    return i18n._('No water at stope');
  })();

  return (
    <Grid size={12} container spacing={1} sx={{ flexGrow: 1 }}>
      <Grid size={6} className={classes.headerTitle}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <WaterDropIcon fontSize="small" color="info" sx={{ mr: 1 }} />
          {warnings.length > 0 && (
            <Box sx={{ mr: 1 }}>
              <WarningTooltipIcon warnings={warnings} />
            </Box>
          )}
          <Box sx={{ flexGrow: 1, wordBreak: 'break-word', overflowWrap: 'break-word', minWidth: 0 }}>
            <Typography sx={{ whiteSpace: 'normal' }}>{flushRecipe.reference}</Typography>
          </Box>
        </Box>
      </Grid>
      <Grid size={6}>
        <Box sx={{ alignContent: 'center' }}>
          <Typography color="textPrimary" variant="caption">
            <UnitSystemValue
              value={result.finalFlushVolume}
              unitInfo={unitLabels[BasicDisplayUnits.Volume]}
              unitSystem={unitSystemPreference}
              decimalPlaces={0}
              customUnitFields={customUnitFields}
            />
            {` | ${timeToWater}`}
          </Typography>
        </Box>
      </Grid>
    </Grid>
  );
};

const confirmDialogOptions = (recipe: BackfillRecipeDto): ConfirmationOptions => ({
  variant: 'danger',
  description: i18n._('Are you sure you want to remove').concat(` '${recipe.reference}'?`),
});

const confirmFlushDialogOptions = (flushRecipe: FlushRecipeDto): ConfirmationOptions => ({
  variant: 'danger',
  description: i18n._('Are you sure you want to remove').concat(` '${flushRecipe.reference}'?`),
});

const gatherWarnings = (recipes: BackfillRecipeDto[]): { [clientId: string]: BackfillWarningDto[] } => {
  const recipesWithWarnings = recipes.filter((recipe) => recipe.warnings && recipe.warnings.length > 0);
  let warnings = {};
  recipesWithWarnings.forEach((recipe) => {
    warnings = {
      ...warnings,
      [recipe.entityId]: recipe.warnings,
    };
  });
  return warnings;
};

const toUpdateBackfillRecipeCommand = (recipe: BackfillRecipeDto): UpdateBackfillRecipeCommand => ({
  reference: recipe.reference,
  rheologyDataSetId: recipe.rheologyDataSetId,
  pasteSpecification: recipe.pasteSpecification,
  pasteOptimisedSpecification: recipe.pasteOptimisedSpecification,
  pasteSelectedSpecification: recipe.pasteSelectedSpecification,
  pasteSlackFlowSpecification: recipe.pasteSlackFlowSpecification,
  pasteMaxPressureSpecification: recipe.pasteMaxPressureSpecification,
  hydraulicSpecification: recipe.hydraulicSpecification,
  hydraulicOptimisedSpecification: recipe.hydraulicOptimisedSpecification,
  hydraulicSelectedSpecification: recipe.hydraulicSelectedSpecification,
  spools: recipe.spoolIds,
  routeSpools: recipe.routeSpoolIds ?? [],
  warnings: recipe.warnings ?? [],
  backfillPlantId: recipe.backfillPlantId,
  pourPresetId: recipe.pourPresetDto?.entityId,
  stopeId: recipe.stopeIdentifier?.stopeId,
  isAutoRecipe: recipe.isAutoRecipe,
  pipeFrictionDataItems: recipe.pipeFrictionDataItems,
});

interface RecipesTabPanelProps {
  model: Model;
  stopeComplete: boolean;
  pourPresetGroupId?: string;
}

const RecipesTabPanel = ({
  model,
  stopeComplete,
  pourPresetGroupId,
}: RecipesTabPanelProps) => {
  const navigate = useNavigate();
  const location = useLocation();

  const confirm = useConfirmation();
  const restoreMutation = useRestoreRecipeMutation();
  const undoDeleteMutation = useDeleteRecipeMutation();
  const undoUpdateMutation = useUpdateRecipeMutation('');
  const {
    actions: {
      setUndoActionOverride,
      clearUndoActionOverride,
    },
  } = usePage();
  const setUndoLoadingState = React.useCallback(() => {
    setUndoActionOverride((current) => (current ? {
      ...current,
      enabled: false,
      loading: true,
    } : current));
  }, [setUndoActionOverride]);
  const registerUndo = React.useCallback((onUndo: () => void) => {
    setUndoActionOverride({
      enabled: true,
      loading: false,
      onClick: onUndo,
    });
  }, [setUndoActionOverride]);
  const publishUndoAction = React.useCallback((recipe: BackfillRecipeDto) => {
    const handleUndo = () => {
      setUndoLoadingState();

      restoreMutation.mutate(recipe.entityId, {
        onSuccess: () => {
          clearUndoActionOverride();
        },
        onError: () => {
          registerUndo(handleUndo);
        },
      });
    };

    registerUndo(handleUndo);
  }, [clearUndoActionOverride, registerUndo, restoreMutation, setUndoLoadingState]);
  const deleteMutation = useDeleteRecipeMutation({
    onSuccess: (_result, recipe) => {
      publishUndoAction(recipe);
    },
  });
  const { project, selectedStope, rheology } = model;
  const hasSelectableRheology = !!firstSelectableRheology(rheology);
  const { entityId, fillType, displayUnitPreferences, customUnitFields, unitSystemPreference, throughput } = project;
  const { activeScenarioId } = useScenario();
  const { state: { enabledModules } } = useProject();
  const flushingEnabled = enabledModules.includes(ProjectModule.Flushing);
  const { data: recipes, isLoading } = useRecipesQuery(
    { projectId: entityId, stopeId: selectedStope?.data.stopeId ?? '', scenarioId: activeScenarioId ?? undefined },
    { enabled: !!selectedStope?.data.stopeId },
  );
  const { data: flushRecipes, isLoading: flushRecipesLoading } = useFlushRecipesQuery(
    { projectId: entityId, stopeId: selectedStope?.data.stopeId ?? '', scenarioId: activeScenarioId ?? undefined },
    { enabled: flushingEnabled && !!selectedStope?.data.stopeId },
  );

  const [newFlush, setNewFlush] = React.useState<boolean>(false);
  const [flushIdEditing, setFlushIdEditing] = React.useState<string | null>(null);
  const [flushIdExpanded, setFlushIdExpanded] = React.useState<string | null>(null);
  const restoreFlushMutation = useRestoreFlushRecipeMutation();
  const undoDeleteFlushMutation = useDeleteFlushRecipeMutation();
  const publishFlushUndoAction = React.useCallback((flushRecipe: FlushRecipeDto) => {
    const handleUndo = () => {
      setUndoLoadingState();

      restoreFlushMutation.mutate(flushRecipe.entityId, {
        onSuccess: () => {
          clearUndoActionOverride();
        },
        onError: () => {
          registerUndo(handleUndo);
        },
      });
    };

    registerUndo(handleUndo);
  }, [clearUndoActionOverride, registerUndo, restoreFlushMutation, setUndoLoadingState]);
  const deleteFlushMutation = useDeleteFlushRecipeMutation({
    onSuccess: (_result, flushRecipe) => {
      publishFlushUndoAction(flushRecipe);
    },
  });

  const {
    isLoading: rheologyDataListIsLoading,
    data: rheologyDataList,
  } = useMineModelRheologyDataListQuery(
    {
      mineModelId: entityId,
      rheologyDataSetIds: [],
    },
  );

  const rheologyDataListIndexedById = rheologyDataList ? indexBy(prop('rheologyDataSetId'), rheologyDataList) : {};
  const rheologyById = React.useMemo<Record<string, RheologyDataDto>>(
    () => (rheologyDataList ? indexBy(prop('rheologyDataSetId'), rheologyDataList) : {}),
    [rheologyDataList],
  );

  const [recipeIdExpanded, setRecipeIdExpanded] = React.useState<string | null>(null);
  const [recipeIdEditing, setRecipeIdEditing] = React.useState<string | null>(null);
  const [newRecipe, setNewRecipe] = React.useState<boolean>(false);
  const [recipeIsOptimised, setRecipeIsOptimised] = React.useState(false);
  const [selectedRheologyDataset, setSelectedRheologyDataset] = React.useState<RheologyDataDto | undefined>(firstSelectableRheology(model.rheology));
  const originalRecipeByIdRef = React.useRef<Record<string, BackfillRecipeDto>>({});
  const handledRecipeLinkRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    if (!model.rheology || model.rheology.length === 0) return;
    // A recipe being edited keeps its own dataset even when deactivated; otherwise fall back to one a new recipe may use.
    setSelectedRheologyDataset((current) => (
      model.rheology.find((dataset) => dataset.rheologyDataSetId === current?.rheologyDataSetId) ?? firstSelectableRheology(model.rheology)
    ));
  }, [model.rheology]);

  React.useEffect(() => {
    const state = location.state as { recipeId?: string } | undefined;
    const linkedRecipeId = state?.recipeId;
    if (!linkedRecipeId || !recipes || handledRecipeLinkRef.current === linkedRecipeId) {
      return;
    }

    const recipe = recipes.find((r) => r.entityId === linkedRecipeId);
    const rheologyDataSet = recipe && rheology?.find((dataset) => dataset.rheologyDataSetId === recipe.rheologyDataSetId);
    if (!recipe || !rheologyDataSet) {
      return;
    }

    setRecipeIdExpanded(linkedRecipeId);
    setRecipeIdEditing(null);
    setNewRecipe(false);
    setRecipeIsOptimised(true);
    handledRecipeLinkRef.current = linkedRecipeId;
    applyRecipeModelVisuals({ model, recipe, rheologyDataSet, fillType });
  }, [location.state, recipes, rheology, model, fillType]);

  // Only the expanded flush drives the HGL overlay; collapsed accordions still render their details.
  const expandedFlushScrub = React.useMemo<FlushScrubContext | undefined>(() => {
    const flushRecipe = flushRecipes?.find((candidate) => candidate.entityId === flushIdExpanded);
    if (!flushRecipe) return undefined;
    const route = findStoredRoute(model, flushRecipe.spoolIds, flushRecipe.routeSpoolIds);
    const linkedRecipe = recipes?.find((recipe) => recipe.entityId === flushRecipe.backfillRecipeId);
    const rheologyDataSet = linkedRecipe && rheologyById[linkedRecipe.rheologyDataSetId];
    const paste = linkedRecipe && rheologyDataSet ? pasteFromRecipe(model, linkedRecipe, rheologyDataSet)?.paste : undefined;
    return route && paste ? { scrubKey: flushRecipe.entityId, route, paste } : undefined;
  }, [flushIdExpanded, flushRecipes, recipes, rheologyById, model.loadGeneration, selectedStope?.data.stopeId]);

  const warnings = React.useMemo(() => gatherWarnings(recipes ?? []), [recipes]);
  const [liveWarnings, setLiveWarnings] = React.useState<BackfillWarningDto[]>();

  const selectedRoute = returnShortestRoute(model.routes);
  const handleAccordionChange = (recipeId: string) => (event: React.ChangeEvent<{}>, isExpanded: boolean) => {
    setRecipeIdExpanded(isExpanded ? recipeId : null);
    setFlushIdExpanded(null);
    setRecipeIsOptimised(true);

    if (isExpanded && recipes) {
      const recipe = recipes.find((r) => r.entityId === recipeId);
      const rheologyDataSet = recipe && rheology?.find((dataset) => dataset.rheologyDataSetId === recipe.rheologyDataSetId);
      if (recipe && rheologyDataSet) {
        applyRecipeModelVisuals({ model, recipe, rheologyDataSet, fillType });
      }
    } else {
      model.setOptimiserResults(undefined);
    }
  };

  const handleNew = () => {
    resetRoutesIfStoredRouteShown(model);
    // Editing a recipe points this at that recipe's dataset, which may be deactivated.
    setSelectedRheologyDataset(firstSelectableRheology(model.rheology));
    setRecipeIsOptimised(false);
    setNewRecipe(true);
    setRecipeIdExpanded(null);
    setRecipeIdEditing(null);
    setNewFlush(false);
    setFlushIdExpanded(null);
  };

  const handleNewFlush = () => {
    resetRoutesIfStoredRouteShown(model);
    setNewFlush(true);
    setFlushIdEditing(null);
    setFlushIdExpanded(null);
    setNewRecipe(false);
    setRecipeIdExpanded(null);
  };

  const handleFlushAccordionChange = (flushRecipeId: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setFlushIdExpanded(isExpanded ? flushRecipeId : null);
    if (!isExpanded) return;

    setRecipeIdExpanded(null);
    const flushRecipe = flushRecipes?.find((candidate) => candidate.entityId === flushRecipeId);
    const route = flushRecipe ? findStoredRoute(model, flushRecipe.spoolIds, flushRecipe.routeSpoolIds) : undefined;
    if (route) {
      model.setSelectedRoute(route);
    }
  };

  const handleFlushSuccess = (flushRecipeId: string) => {
    if (newFlush && flushRecipeId) {
      const handleUndoCreate = () => {
        setUndoLoadingState();

        undoDeleteFlushMutation.mutate({ entityId: flushRecipeId } as FlushRecipeDto, {
          onSuccess: () => {
            clearUndoActionOverride();
          },
          onError: () => {
            registerUndo(handleUndoCreate);
          },
        });
      };

      registerUndo(handleUndoCreate);
    }

    setNewFlush(false);
    setFlushIdEditing(null);
    setFlushIdExpanded(null);
  };

  const handleFlushCancel = () => {
    setNewFlush(false);
    setFlushIdEditing(null);
  };

  const handleFlushDelete = (flushRecipe: FlushRecipeDto) => {
    confirm(confirmFlushDialogOptions(flushRecipe))
      .then(() => deleteFlushMutation.mutate(flushRecipe));
  };

  const handleNewPour = (recipe: BackfillRecipeDto) => {
    model.setRecipeState({ ...model.recipeState, recipeTab: 'pours' });
    navigate(
      resolveTemplate(
        NewPourRoutePath,
        {
          [projectParamIdentifier]: entityId,
          [stopeParamIdentifier]: selectedStope!.data.stopeId,
          [recipeParamIdentifier]: recipe.entityId,
        },
      ),
    );
  };

  const handleRecipeWarning = (newWarnings: BackfillWarningDto[]) => {
    if (newWarnings && newWarnings.length > 0) {
      setLiveWarnings(newWarnings);
    } else {
      setLiveWarnings(undefined);
    }
  };

  const handleSuccess = (recipeId: string): void => {
    const wasCreatingRecipe = newRecipe;
    const editedRecipeSnapshot = (!wasCreatingRecipe && recipeId)
      ? originalRecipeByIdRef.current[recipeId]
      : null;

    if (wasCreatingRecipe && recipeId) {
      const handleUndoCreate = () => {
        setUndoLoadingState();

        undoDeleteMutation.mutate({ entityId: recipeId } as BackfillRecipeDto, {
          onSuccess: () => {
            clearUndoActionOverride();
          },
          onError: () => {
            registerUndo(handleUndoCreate);
          },
        });
      };

      registerUndo(handleUndoCreate);
    } else if (editedRecipeSnapshot && recipeId) {
      const previousCommand = toUpdateBackfillRecipeCommand(editedRecipeSnapshot);

      const handleUndoEdit = () => {
        setUndoLoadingState();

        undoUpdateMutation.mutate({
          projectId: entityId,
          recipeId,
          data: [previousCommand],
          context: { stopeName: [selectedStope?.data?.stopeName ?? ''] },
        }, {
          onSuccess: () => {
            clearUndoActionOverride();
          },
          onError: () => {
            registerUndo(handleUndoEdit);
          },
        });
      };

      registerUndo(handleUndoEdit);
    }

    setNewRecipe(false);
    setRecipeIdEditing(null);
    setRecipeIdExpanded(null);
  };

  const handleEdit = (recipeId: string) => {
    setRecipeIdEditing(recipeId);
    const selectedRecipe = recipes && recipes.find((r) => r.entityId === recipeId);
    if (selectedRecipe) {
      originalRecipeByIdRef.current[recipeId] = structuredClone(selectedRecipe);
    }
    const rheologyDataSet = selectedRecipe && rheology?.find((dataset) => dataset.rheologyDataSetId === selectedRecipe.rheologyDataSetId);
    rheologyDataSet && setSelectedRheologyDataset(rheologyDataSet as any);
  };

  const handleDelete = (recipe: BackfillRecipeDto) => {
    confirm(confirmDialogOptions(recipe))
      .then(() => deleteMutation.mutate(recipe));
  };

  const handleCancel = (recipeId?: string) => () => {
    if (recipeId) {
      setRecipeIdEditing(null);
    } else {
      setNewRecipe(false);
    }
    setRecipeIdExpanded(null);
  };

  const handleRecipeIsOptimisedUpdate = (isOptimised: boolean) => {
    setRecipeIsOptimised(isOptimised);
  };

  if (isLoading || rheologyDataListIsLoading || (flushingEnabled && flushRecipesLoading)) {
    return <ListSkeleton numberOfListItems={6} />;
  }

  if (!recipes) {
    return <AlertText severity="info">{i18n._('No recipes have been created.')}</AlertText>;
  }

  return (
    (
      <Root className={classes.root}>
        <ProjectAuthorise permission={Permission.ManageRecipes}>
          <Box
            sx={{
              mx: 3,
              mb: 2,
            }}
          >
            {
              selectedRoute && (
                // Grid, not flex: a disabled ConditionalButton sits inside a tooltip Box that won't take an equal share of a flex row,
                // so its label wrapped and stretched both buttons to double height.
                <Box sx={{ display: 'grid', gridTemplateColumns: flushingEnabled ? '1fr 1fr' : '1fr', gap: 1 }}>
                  <ConditionalButton
                    fullWidth
                    variant="contained"
                    color="secondary"
                    size={flushingEnabled ? 'medium' : 'large'}
                    startIcon={<AddIcon />}
                    onClick={() => handleNew()}
                    disabled={!hasSelectableRheology}
                    disabledReason={i18n._('Add rheology data before creating a recipe')}
                  >
                    <Trans id="Add Recipe">Add Recipe</Trans>
                  </ConditionalButton>
                  {flushingEnabled && (
                    <ConditionalButton
                      fullWidth
                      variant="contained"
                      color="secondary"
                      size="medium"
                      startIcon={<WaterDropIcon />}
                      onClick={() => handleNewFlush()}
                      disabled={recipes.length === 0}
                      disabledReason={i18n._('Add a recipe before creating a flush')}
                    >
                      <Trans id="Add Flush">Add Flush</Trans>
                    </ConditionalButton>
                  )}
                </Box>
              )
            }
            {selectedRoute && !hasSelectableRheology && (
              <Alert severity="info" sx={{ mt: 1 }}>
                <Trans id="No rheology data available. Please add rheology data to create recipes.">
                  No rheology data available. Please add rheology data to create recipes.
                </Trans>
              </Alert>
            )}
          </Box>
        </ProjectAuthorise>
        {/* Create recipe */}
        {newRecipe && (
          <Accordion expanded={newRecipe} onChange={() => setNewRecipe(false)}>
            <AccordionSummary
              expandIcon={<CloseIcon fontSize="large" />}
              id="panel-new-header"
            >
              {liveWarnings && <WarningTooltipIcon warnings={liveWarnings} />}
              <Box sx={{
                pl: 1,
              }}
              >
                <Typography variant="h6">
                  <Trans id="New Stope Recipe">New Stope Recipe</Trans>
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                sx={{
                  width: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <RecipeForm
                  model={model}
                  selectedDataset={selectedRheologyDataset as RheologyDataDto}
                  stopeId={[selectedStope as PCStope]}
                  stopeName={[selectedStope?.data?.stopeName as string]}
                  isAutoRecipe={false}
                  pourPresetGroupId={pourPresetGroupId}
                  isOptimised={recipeIsOptimised}
                  onSuccess={handleSuccess}
                  checkForWarnings={() => {
                    const route = model.getSelectedRoute?.();
                    if (!route) return [];
                    return route.findWarnings?.() ?? route.warnings ?? [];
                  }}
                  onCancel={handleCancel()}
                  onWarning={handleRecipeWarning}
                  updateIsOptimised={handleRecipeIsOptimisedUpdate}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
        {/* Create flush */}
        {flushingEnabled && newFlush && (
          <Accordion expanded={newFlush} onChange={() => setNewFlush(false)}>
            <AccordionSummary
              expandIcon={<CloseIcon fontSize="large" />}
              id="flush-panel-new-header"
            >
              <Box sx={{ pl: 1, display: 'flex', alignItems: 'center' }}>
                <WaterDropIcon fontSize="small" color="info" sx={{ mr: 1 }} />
                <Typography variant="h6">
                  <Trans id="New Flush">New Flush</Trans>
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column' }}>
                <FlushForm
                  model={model}
                  recipes={recipes}
                  rheologyById={rheologyById}
                  onSuccess={handleFlushSuccess}
                  onCancel={handleFlushCancel}
                />
              </Box>
            </AccordionDetails>
          </Accordion>
        )}
        {/* View, edit and delete recipes */}
        {!rheologyDataListIsLoading && recipes.map((recipe, index) => (
          <React.Fragment key={recipe.entityId}>
            {
              recipe.rheologyDataSetId && rheologyDataListIndexedById[recipe.rheologyDataSetId ?? '']
                ? (
                  <Accordion expanded={recipeIdExpanded === recipe.entityId} onChange={handleAccordionChange(recipe.entityId)}>
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      id={`panel-${index}-header`}
                    >
                      <RecipeSummary
                        isAutoRecipe={recipe.isAutoRecipe}
                        reference={recipe.reference}
                        warnings={recipe.entityId === recipeIdEditing ? liveWarnings : warnings?.[recipe.entityId]}
                        isStale={recipe.isStale}
                        optimisedMassConcentration={recipe.pasteOptimisedSpecification?.massConcentration ?? 0}
                        selectedMassConcentration={recipe.pasteSelectedSpecification?.massConcentration ?? 0}
                        optimisedBinderContent={recipe.pasteOptimisedSpecification?.binderContent ?? 0}
                        selectedBinderContent={recipe.pasteSelectedSpecification?.binderContent ?? 0}
                      />
                      {recipe.isStale && <Backdrop className={classes.backdrop} open />}
                    </AccordionSummary>
                    <AccordionDetails className={classes.accordionDetails}>
                      <Box>
                        {recipe.isStale && (
                          <Alert severity="warning">
                            <Trans id="The stope information has been modified after this recipe was created.">
                              The stope information has been modified after this recipe was created.
                              To create a pour from this recipe you are required to edit and re-optimise the values.
                            </Trans>
                          </Alert>
                        )}
                      </Box>
                      <Box>
                        {
                          recipeIdEditing === recipe.entityId
                            && recipe.rheologyDataSetId
                            && rheologyDataListIndexedById[recipe.rheologyDataSetId ?? '']
                            ? (
                              <>
                                <RecipeForm
                                  key={recipe.entityId}
                                  model={model}
                                  selectedDataset={selectedRheologyDataset as RheologyDataDto}
                                  recipe={recipe}
                                  stopeId={[selectedStope as PCStope]}
                                  stopeName={[selectedStope?.data?.stopeName as string]}
                                  isAutoRecipe={false}
                                  pourPresetGroupId={pourPresetGroupId}
                                  isOptimised={recipeIsOptimised}
                                  onSuccess={handleSuccess}
                                  checkForWarnings={() => {
                                    const route = model.getSelectedRoute?.();
                                    if (!route) return [];
                                    return route.findWarnings?.() ?? route.warnings ?? [];
                                  }}
                                  onCancel={handleCancel()}
                                  onWarning={handleRecipeWarning}
                                  updateIsOptimised={handleRecipeIsOptimisedUpdate}
                                />
                              </>
                            ) : (
                              <>
                                <RecipeDetails
                                  recipe={recipe}
                                  displayUnitPreferences={displayUnitPreferences}
                                  customUnitFields={customUnitFields}
                                  unitSystemPreference={unitSystemPreference}
                                  throughputControlType={throughput}
                                  targetDaysCoefficients={rheologyDataListIndexedById[recipe.rheologyDataSetId ?? ''].ucsCoefficients ?? {}}
                                  specificGravity={recipe.rheologyDataSetId ? rheologyDataListIndexedById[recipe.rheologyDataSetId ?? ''].tailingsDrySolidsDensity : undefined}
                                  mixerCoefficients={recipe.rheologyDataSetId ? rheologyDataListIndexedById[recipe.rheologyDataSetId ?? ''].mixerCoefficients : undefined}
                                  heightOfCylinder={recipe.rheologyDataSetId ? rheologyDataListIndexedById[recipe.rheologyDataSetId ?? ''].heightOfCylinder : undefined}
                                  yieldStressA={recipe.rheologyDataSetId
                                    ? rheologyDataListIndexedById[recipe.rheologyDataSetId ?? ''].yieldStressCoefficients.coefficient1
                                    : undefined}
                                  yieldStressB={recipe.rheologyDataSetId
                                    ? rheologyDataListIndexedById[recipe.rheologyDataSetId ?? ''].yieldStressCoefficients.coefficient2
                                    : undefined}
                                  tailingsSolidsDensity={rheologyDataListIndexedById[recipe.rheologyDataSetId ?? ''].tailingsDrySolidsDensity}
                                  carrierFluidDensity={rheologyDataListIndexedById[recipe.rheologyDataSetId ?? ''].carrierFluidDensity}
                                  binderParticleDensity={rheologyDataListIndexedById[recipe.rheologyDataSetId ?? ''].binderDryParticleDensity}
                                  fillType={fillType}
                                  backFillPlantNodeId={(recipe.backfillPlantId)}
                                  pumpType={project.pumpType}
                                  boosterPumpNodes={(() => {
                                    const route = selectedRoute;
                                    if (!route) return [];
                                    const boosterPoints = getPointsWithBoosterPressure(route);
                                    return boosterPoints.map((rp) => ({
                                      nodeId: rp.point.id,
                                      pressure: pumpPressureKpaToBar(rp.pressure ?? 0),
                                    }));
                                  })()}
                                />
                                <Box sx={{
                                  mt: 1,
                                }}
                                >
                                  <ProjectAuthorise
                                    permission={Permission.ManageRecipes}
                                    render={(isAuthorised) => (
                                      <>
                                        {isAuthorised ? (
                                          <Actions
                                            left={[
                                              ['delete', (
                                                <Button className={classes.deleteButton} onClick={() => handleDelete(recipe)}>
                                                  <Trans id="Delete">Delete</Trans>
                                                </Button>
                                              )],
                                              ['edit', (
                                                <Button color="secondary" onClick={() => handleEdit(recipe.entityId)}>
                                                  <Trans id="Edit">Edit</Trans>
                                                </Button>
                                              )],
                                            ]}
                                            right={[
                                              ['create', (
                                                <ConditionalButton
                                                  color="secondary"
                                                  onClick={() => handleNewPour(recipe)}
                                                  disabled={stopeComplete || recipe.isStale}
                                                  disabledReason={stopeComplete ? i18n._('Stope is complete') : i18n._('This recipe is stale and has been disabled')}
                                                >
                                                  <Trans id="Create Pour">Create Pour</Trans>
                                                </ConditionalButton>
                                              )],
                                            ]}
                                          />
                                        ) : (
                                          <ProjectAuthorise permission={Permission.ManagePours}>
                                            <Actions
                                              right={[
                                                ['create', (
                                                  <ConditionalButton
                                                    color="secondary"
                                                    onClick={() => handleNewPour(recipe)}
                                                    disabled={stopeComplete || recipe.isStale}
                                                    disabledReason={stopeComplete ? i18n._('Stope is complete') : i18n._('This recipe is stale and has been disabled')}
                                                  >
                                                    <Trans id="Create Pour">Create Pour</Trans>
                                                  </ConditionalButton>
                                                )],
                                              ]}
                                            />
                                          </ProjectAuthorise>
                                        )}
                                      </>
                                    )}
                                  />
                                </Box>
                              </>
                            )
                        }
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                )
                : (
                  <RecipeErroredPlaceHolder recipe={recipe} message={i18n._('The rheology data set for this recipe has been deleted')} onDelete={() => handleDelete(recipe)} />
                )
            }
          </React.Fragment>
        ))}
        {/* View, edit and delete flushes */}
        {flushingEnabled && (flushRecipes ?? []).map((flushRecipe, index) => (
          <Accordion
            key={flushRecipe.entityId}
            expanded={flushIdExpanded === flushRecipe.entityId}
            onChange={handleFlushAccordionChange(flushRecipe.entityId)}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              id={`flush-panel-${index}-header`}
            >
              <FlushSummary
                flushRecipe={flushRecipe}
                unitSystemPreference={unitSystemPreference}
                customUnitFields={customUnitFields}
              />
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>
              {flushIdEditing === flushRecipe.entityId
                ? (
                  <FlushForm
                    key={flushRecipe.entityId}
                    model={model}
                    flushRecipe={flushRecipe}
                    recipes={recipes}
                    rheologyById={rheologyById}
                    onSuccess={handleFlushSuccess}
                    onCancel={handleFlushCancel}
                  />
                ) : (
                  <>
                    <FlushDetails
                      specification={flushRecipe.specification}
                      result={flushRecipe.result}
                      linkedRecipeReference={recipes.find((recipe) => recipe.entityId === flushRecipe.backfillRecipeId)?.reference}
                      unitSystemPreference={unitSystemPreference}
                      customUnitFields={customUnitFields}
                      scrub={flushIdExpanded === flushRecipe.entityId ? expandedFlushScrub : undefined}
                    />
                    <Box sx={{ mt: 1 }}>
                      <ProjectAuthorise permission={Permission.ManageRecipes}>
                        <Actions
                          left={[
                            ['delete', (
                              <Button className={classes.deleteButton} onClick={() => handleFlushDelete(flushRecipe)}>
                                <Trans id="Delete">Delete</Trans>
                              </Button>
                            )],
                            ['edit', (
                              <Button color="secondary" onClick={() => setFlushIdEditing(flushRecipe.entityId)}>
                                <Trans id="Edit">Edit</Trans>
                              </Button>
                            )],
                          ]}
                        />
                      </ProjectAuthorise>
                    </Box>
                  </>
                )}
            </AccordionDetails>
          </Accordion>
        ))}
      </Root>
    )
  );
};

export default RecipesTabPanel;
